import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, TrendingUp, Users, MessageCircle, Clock, Activity } from 'lucide-react';

const UsageAnalytics = () => {
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');
    const { t } = useLanguage();

    useEffect(() => {
        loadUsageData();
    }, [period]);

    const loadUsageData = async () => {
        try {
            setLoading(true);
            const response = await analyticsService.getAIUsageStats(period);
            if (response.success) {
                setUsageData(response.data);
            }
        } catch (error) {
            console.error(t('loadStatsError'), error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <div>{t('loading')}</div>
            </div>
        );
    }

    if (!usageData) {
        return <div className="no-data">{t('noStatsAvailable')}</div>;
    }

    const generalStats = usageData.generalStatistics;
    const planStats = usageData.planStatistics;

    return (
        <div className="usage-analytics">
            {/* Period Selector */}
            <div className="period-selector">
                <div className="period-buttons">
                    {['today', 'week', 'month', 'year'].map(p => (
                        <button
                            key={p}
                            className={`period-button ${period === p ? 'active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {t(p)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Usage Overview */}
            <div className="usage-overview">
                <div className="usage-card">
                    <div className="usage-icon">
                        <MessageCircle size={28} />
                    </div>
                    <div className="usage-content">
                        <h3>{generalStats?.total_messages?.toLocaleString() || 0}</h3>
                        <p>{t('totalMessages')}</p>
                    </div>
                </div>
                <div className="usage-card">
                    <div className="usage-icon">
                        <Users size={28} />
                    </div>
                    <div className="usage-content">
                        <h3>{generalStats?.active_users?.toLocaleString() || 0}</h3>
                        <p>{t('activeUsers')}</p>
                    </div>
                </div>
                <div className="usage-card">
                    <div className="usage-icon">
                        <TrendingUp size={28} />
                    </div>
                    <div className="usage-content">
                        <h3>{(generalStats?.avg_tokens_per_message || 0).toFixed(0)}</h3>
                        <p>{t('avgTokensPerMessage')}</p>
                    </div>
                </div>
                <div className="usage-card">
                    <div className="usage-icon">
                        <Clock size={28} />
                    </div>
                    <div className="usage-content">
                        <h3>{(generalStats?.total_tokens || 0).toLocaleString()}</h3>
                        <p>{t('totalTokens')}</p>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
                <div className="chart-card">
                    <div className="chart-header">
                        <h4>{t('usageOverTime')}</h4>
                        <Calendar size={20} />
                    </div>
                    <div className="chart-placeholder">
                        <p>📊 {t('chartPlaceholder')}</p>
                        <div className="chart-legend">
                            <div className="legend-item">
                                <span className="legend-color blue"></span>
                                <span>{t('messages')}</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-color green"></span>
                                <span>{t('tokens')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h4>{t('planDistribution')}</h4>
                        <Users size={20} />
                    </div>
                    <div className="chart-placeholder">
                        <div className="plan-distribution">
                            {planStats?.map(plan => (
                                <div key={plan.plan} className="plan-item">
                                    <span className={`plan-label ${plan.plan}`}>{t(plan.plan)}</span>
                                    <span className="plan-value">{plan.message_count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="chart-card full-width">
                    <div className="chart-header">
                        <h4>{t('userActivity')}</h4>
                        <Activity size={20} />
                    </div>
                    <div className="chart-placeholder large">
                        <p>📈 {t('userActivity')} - {t('chartPlaceholder')}</p>
                        <div className="activity-stats">
                            <div className="activity-metric">
                                <span>{t('peakUsage')}</span>
                                <strong>14:00 - 16:00</strong>
                            </div>
                            <div className="activity-metric">
                                <span>{t('avgDailySessions')}</span>
                                <strong>3.2</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UsageAnalytics;